import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toolsApi } from "@/lib/toolsApi";
import { useToolsAuth } from "@/context/ToolsAuthContext";

const MAX_POLLS = 15;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useToolsAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const pollRef = useRef(0);
  const isDownloads = params.get("next") === "downloads";

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (pollRef.current >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      pollRef.current += 1;
      try {
        const { data } = await toolsApi.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [params, refresh]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-cream px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full text-center border border-gold/40 bg-white/50 p-10"
      >
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin text-terracotta mx-auto" size={36} />
            <h1 className="mt-6 font-serif font-light text-3xl text-wine">Confirming your purchase…</h1>
            <p className="mt-3 text-sm text-charcoal/60">This only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="text-terracotta mx-auto" size={44} />
            <h1 className="mt-6 font-serif font-light text-3xl text-wine">You&apos;re in.</h1>
            <p className="mt-3 text-sm text-charcoal/70">
              {isDownloads ? "Your purchase is ready in your library." : "Your toolkit is unlocked and ready."}
            </p>
            <button
              onClick={() => navigate(isDownloads ? "/downloads" : "/tools")}
              data-testid="go-to-tools"
              className="mt-8 rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-wine transition-colors"
            >
              {isDownloads ? "Go to my downloads" : "Open my tools"}
            </button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="text-wine mx-auto" size={44} />
            <h1 className="mt-6 font-serif font-light text-3xl text-wine">
              {status === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-3 text-sm text-charcoal/70">
              {status === "timeout"
                ? "Your payment is taking longer than usual. Check your tools in a minute — it may already be active."
                : "We couldn't confirm your payment. If you were charged, please contact us."}
            </p>
            <Link
              to={isDownloads ? "/downloads" : "/tools"}
              data-testid="back-to-tools"
              className="mt-8 inline-block rounded-full border border-terracotta text-terracotta px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-terracotta hover:text-cream transition-colors"
            >
              {isDownloads ? "Back to downloads" : "Back to tools"}
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
